/**
 * PathInk (BL-287) — the enemy conduit's colour, and the **temporary** review
 * switch that lets the owner settle it on a real device.
 *
 * ## Why this module exists at all
 *
 * The conduit green was the only element on the board outside the palette:
 * hardcoded twice in `ProjectileRenderer` while the packets on the same object
 * already used `SENTINEL_GREEN`. The obvious fix was to re-tone it the way D-237
 * did everywhere else. The owner declined, and was right to — the requirement
 * says the green data path must be **preserved**, and the path is the one
 * element whose whole job is to be unmistakable at a glance. Legibility outranks
 * palette tidiness, so the **drift** is what gets fixed here, not the colour:
 * both candidates are named constants with one place that decides between them.
 *
 * ## Why it is switchable at runtime rather than at build time
 *
 * The owner asked to compare the two **in the running game on an iPhone**, not
 * in a mockup — and a Capacitor app has no address bar, so the `?path=` query
 * parameter that serves the web build is unreachable there. Hence a live setter
 * and a review chip (`PathInkReviewChip`).
 *
 * **This is scaffolding with a delete date.** Owner: *"Nach der Entscheidung
 * kann der Schalter wieder raus."* When the verdict is in, the losing constant,
 * this module's setter and the chip all go; the winner becomes the single token.
 *
 * ## Cost
 *
 * The ramps are recomputed **on change**, never per frame — a switch happens at
 * most a handful of times in a session, and the renderer reads two already-built
 * strings. Nothing here is in the hot path.
 */

import { COLORS } from "../core/constants.ts";

export type PathInkVariant = "neon" | "sentinel";

/** The shipped conduit green. Kept at its exact value — see the module note. */
export const PATH_INK_NEON = COLORS.PATH_GREEN;
/** The palette's own green, the alternative under review. */
export const PATH_INK_SENTINEL = COLORS.SENTINEL_GREEN;

/** Hex → rgba (the `CorruptionLayer` idiom), so the alpha ramps are derived
 *  FROM the constant instead of being written out as literals. */
function rgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** The two alphas the conduit is drawn at — unchanged from what shipped. */
const OUTER_ALPHA = 0.12;
const INNER_ALPHA = 0.35;

let variant: PathInkVariant = "neon";
let outer = rgba(PATH_INK_NEON, OUTER_ALPHA);
let inner = rgba(PATH_INK_NEON, INNER_ALPHA);

/**
 * Switch the conduit ink. Recomputes the two ramps once; callers read them per
 * frame. Unknown values are ignored rather than producing a third variant.
 */
export function setPathInkVariant(next: PathInkVariant): void {
  if (next !== "neon" && next !== "sentinel") return;
  variant = next;
  const hex = next === "sentinel" ? PATH_INK_SENTINEL : PATH_INK_NEON;
  outer = rgba(hex, OUTER_ALPHA);
  inner = rgba(hex, INNER_ALPHA);
}

export function pathInkVariant(): PathInkVariant {
  return variant;
}

/** The conduit's outer glow ramp for this frame. */
export function pathGlowOuter(): string {
  return outer;
}

/** The conduit's inner dashed ramp for this frame. */
export function pathGlowInner(): string {
  return inner;
}

/**
 * Seed the variant from `?path=` on the web build. The native shell has no
 * address bar, which is exactly why the chip exists; there the default holds
 * until the owner taps it. Guarded so headless contexts (tests, the
 * golden-master harness) always take the shipped default.
 */
export function initPathInkFromLocation(): void {
  try {
    if (typeof location !== "undefined" && location.search) {
      const v = new URLSearchParams(location.search).get("path");
      if (v === "sentinel" || v === "neon") setPathInkVariant(v);
    }
  } catch {
    /* non-DOM — keep the shipped default */
  }
}

/**
 * Should this build carry the review switch? Asymmetric on purpose, because
 * merging to `main` auto-deploys the web build to the public site:
 *
 *   - **native shell (TestFlight)** — always. There is no address bar, so a
 *     query parameter cannot reach it, and that build exists to be reviewed.
 *   - **web** — only with `?review=1`, so players never see it.
 *
 * Lives here rather than in the chip so the mount site and the chip itself
 * cannot drift apart on the one question that decides who sees a review control.
 */
export function shouldShowPathInkReview(isNative: boolean): boolean {
  if (isNative) return true;
  try {
    if (typeof location !== "undefined" && location.search) {
      return new URLSearchParams(location.search).get("review") === "1";
    }
  } catch {
    /* non-DOM */
  }
  return false;
}

/** Test-only: restore the shipped default so a suite cannot leak a variant. */
export function __resetPathInkForTest(): void {
  setPathInkVariant("neon");
}
